import * as z from 'zod';

import { BankruptcyId, ReportableEventId } from '../BrandedIds';
import { zodJSInstantDate, zodJSPlainDate } from '../dates/BrandedDate';
import { AuthorizedContactPaymentPayer, BorrowerPaymentPayer, EntityPaymentPayer } from './Payments';

export const BankruptcyChapter = z.enum(['CHAPTER_7', 'CHAPTER_11', 'CHAPTER_12', 'CHAPTER_13']);
export type BankruptcyChapter = z.infer<typeof BankruptcyChapter>;

export const BankruptcyStatus = z.enum(['active', 'closed', 'archived']);
export type BankruptcyStatus = z.infer<typeof BankruptcyStatus>;

export const BankruptcyOutcome = z.enum(['dismissed', 'discharged', 'converted', 'other']);
export type BankruptcyOutcome = z.infer<typeof BankruptcyOutcome>;

export const BankruptcyFiledBy = z.discriminatedUnion('type', [
  BorrowerPaymentPayer,
  EntityPaymentPayer,
  AuthorizedContactPaymentPayer,
]);
export type BankruptcyFiledBy = z.infer<typeof BankruptcyFiledBy>;

export const Bankruptcy = z.object({
  id: BankruptcyId,
  chapter: BankruptcyChapter,
  filedAt: zodJSPlainDate,
  filedBy: BankruptcyFiledBy.array().min(1, { message: 'At least one filer required' }).optional(), // TODO make required when migration has run
  // @migrationTodo: remove default after `20260427migrateFCandBK` runs in prod
  status: BankruptcyStatus.default('active'),
  statusHistory: z
    .object({
      status: BankruptcyStatus,
      changedAt: zodJSInstantDate,
    })
    .array()
    // @migrationTodo: remove default after `20260427migrateFCandBK` runs in prod
    .default([]),
  archivedAt: zodJSInstantDate.optional(),

  /** date bankruptcy exited active status */
  endDate: zodJSPlainDate.optional(),

  /** outcome of a cancelled or archived bankruptcy */
  endOutcome: BankruptcyOutcome.optional(),

  /**
   * @deprecated use `endDate` for the close date and `updatedAt` for "last touched".
   * Kept in the schema temporarily so `20260427migrateFCandBK` can lift legacy values
   * into `endDate` without Zod stripping them on parse. Drop in a follow-up PR after
   * the migration has run in prod.
   */
  completedAt: zodJSPlainDate.optional(),

  reportableEventIds: z.array(ReportableEventId).default([]), // @migrationTodo: remove soon

  createdAt: zodJSInstantDate,
  updatedAt: zodJSInstantDate,
});
export type Bankruptcy = z.infer<typeof Bankruptcy>;

export const BankruptcyInput = Bankruptcy.pick({
  id: true,
  chapter: true,
  filedAt: true,
  filedBy: true,
  endDate: true,
}).partial({ id: true });
export type BankruptcyInput = z.infer<typeof BankruptcyInput>;
